import memoize from '@jetbrains/ring-ui/components/global/memoize'
import classNames from 'classnames'
import type {HTMLAttributes} from 'react'
import {useLayoutEffect, useRef} from 'react'

import {DEFAULT_GRID_ID} from './Grid.consts'

import styles from './Grid.css'

export type GridProps = HTMLAttributes<HTMLElement> & {
  gridId?: string
}

// gridId - for the case when you use a sub grid
function Grid({gridId = DEFAULT_GRID_ID, children, className, ...restProps}: GridProps) {
  const gridKeySelector = `[data-grid-id=${gridId}]`
  const rowGroupSelector = `${gridKeySelector}[data-grid-row-span]`
  const rowGroupsWrapperSelector = `${gridKeySelector}[data-grid-row-groups-wrapper]`

  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const root = ref.current

    if (root == null) {
      return undefined
    }

    const updateGridRows = () => {
      const getSpan = memoize((element: HTMLElement) => Number(element.dataset.gridRowSpan))
      let currentRow = 1
      const rowGroups = [...root.querySelectorAll<HTMLElement>(rowGroupSelector)].filter(
        element => element.closest('[data-grid-root]') === root,
      )
      rowGroups.forEach(element => {
        element.style.setProperty('--row', String(currentRow))
        currentRow += getSpan(element)
      })
      ;[...root.querySelectorAll<HTMLElement>(rowGroupsWrapperSelector)]
        .filter(element => element.closest('[data-grid-root]') === root)
        .forEach(group => {
          let row = 1
          let span = 0
          rowGroups.forEach(element => {
            if (group.contains(element)) {
              span += getSpan(element)
            } else if (span === 0) {
              row += getSpan(element)
            }
          })
          group.style.setProperty('--row', String(row))
          group.style.setProperty('--row-span', String(span))
        })
    }

    updateGridRows()
    const observer = new MutationObserver(mutations => {
      if (
        mutations.some(mutation =>
          [...mutation.addedNodes, ...mutation.removedNodes].some(
            node =>
              (node instanceof Element && node.matches(rowGroupSelector)) ||
              ((node instanceof Element || node instanceof DocumentFragment) &&
                node.querySelector(rowGroupSelector) != null),
          ),
        )
      ) {
        updateGridRows()
      }
    })
    observer.observe(root, {
      childList: true,
      subtree: true,
    })
    return () => observer.disconnect()
  }, [rowGroupSelector, rowGroupsWrapperSelector])
  const classes = classNames(styles.grid, className)
  return (
    <div>
      <div data-grid-root {...restProps} className={classes} ref={ref}>
        {children}
      </div>
    </div>
  )
}

export default Grid
